import React, { Component, forwardRef, createRef, type Ref, type MouseEvent, type UIEvent } from 'react';
import { mergeRefs } from 'lib/react';

import cn from 'clsx';
import * as css from './HeaderDropdown.module.scss';

import { CaretDown } from '@carbon/icons-react';
import { HeaderMenu, HeaderMenuItem, InlineLoading } from '@carbon/react';

export type HeaderDropdownMenuItem =
    | string
    | {
          icon?: false | ReactNode;
          onClick(): void;
          label: string;
          className?: string;
          disabled?: boolean;
          loading?: boolean;
      };
export type HeaderDropdownProps = {
    arrow?: boolean;
    direction?: 'left' | 'right';

    expanded?: boolean;
    onToggle?(): void;

    toggle: NonNullable<ReactNode>;
    items: Array<null | HeaderDropdownMenuItem> | JSX.Element;

    title: string;
    label?: null | string;
    className?: string;
    iconClassName?: string;

    // eslint-disable-next-line ssr-friendly/no-dom-globals-in-module-scope
    innerRef?: Ref<HTMLAnchorElement>;
};

function stopEvent(e: Maybe<MouseEvent>): void {
    e?.preventDefault();
    e?.stopPropagation();
}

class HeaderDropdown extends Component<HeaderDropdownProps> {
    private ref = createRef<HTMLAnchorElement>();

    componentDidMount() {
        const el = this.ref.current;
        if (this.props.expanded && el) el.setAttribute('aria-expanded', 'true');
    }
    componentDidUpdate(prevProps: HeaderDropdownProps) {
        const { expanded } = this.props;
        const el = this.ref.current;
        if (el && typeof expanded === 'boolean' && expanded !== prevProps.expanded)
            el.setAttribute('aria-expanded', String(expanded));
    }

    // We have an aggressive click handler to be able to overtake the default functionality
    // of underlying components that closes the menu whenever anything inside is clicked.
    private handleClick = (e: UIEvent): void => {
        const { expanded, onToggle } = this.props;

        // Only catch clicks on the toggle anchor
        if (e.target !== this.ref.current) return;

        // Stop & abort if we're in a controlled state
        if (typeof expanded === 'boolean') {
            e.stopPropagation();
            e.preventDefault();
        }

        // Call the toggle handler
        if (onToggle) onToggle();
    };

    render() {
        const { items, arrow = true, direction, toggle, title, label, className, iconClassName, innerRef } = this.props;

        return (
            <HeaderMenu
                ref={mergeRefs(innerRef, this.ref)}
                // @ts-expect-error: `onClickCapture` is not in the types
                onClickCapture={this.handleClick}
                className={cn(css.root, css[direction ?? 'left'], className)}
                aria-label={title}
                menuLinkName={label || ''}
                renderMenuContent={() => (
                    <>
                        {toggle}
                        {arrow && <CaretDown size={16} name="angle-down-thin" className={css.arrowIcon} />}
                    </>
                )}
                children={
                    Array.isArray(items)
                        ? items.map((p, i) => {
                              if (!p) return null;

                              if (typeof p === 'string') {
                                  return (
                                      <div
                                          key={`title-${i}`}
                                          className={css.labelItem}
                                          children={<span children={p} />}
                                      />
                                  );
                              }

                              let icon: ReactNode = '\xa0';
                              if (p.icon != null) icon = p.icon;

                              const isDisabled = p.disabled || p.loading;

                              return (
                                  <HeaderMenuItem
                                      onClick={!isDisabled ? p.onClick : stopEvent}
                                      children={
                                          <>
                                              {p.loading ? (
                                                  <InlineLoading status="active" className={css.itemLoadingIcon} />
                                              ) : (
                                                  p.icon !== false && (
                                                      <div
                                                          className={cn(css.itemIcon, iconClassName)}
                                                          children={icon}
                                                      />
                                                  )
                                              )}
                                              <span children={p.label} />
                                          </>
                                      }
                                      className={cn(
                                          css.item,
                                          p.className,
                                          isDisabled && css.disabled,
                                          i > 0 && items[i - 1] == null && css.itemSpaced,
                                      )}
                                      key={i}
                                  />
                              );
                          })
                        : items
                }
            />
        );
    }
}

// eslint-disable-next-line ssr-friendly/no-dom-globals-in-module-scope
export default forwardRef<HTMLAnchorElement, HeaderDropdownProps>((props, ref) => (
    <HeaderDropdown {...props} innerRef={ref} />
));
